import { deleteData, getData, postData } from "../api/api";

export type TicketType = "SUPPORT" | "FEATURE" | "CHANGE" | "BUG";
export type TicketStatus =
  | "OPEN"
  | "ANALYZING"
  | "ANALYZED"
  | "NEEDS_CLARIFICATION"
  | "SUPPORT_QUEUE"
  | "CONFLICT_REVIEW"
  | "AUTO_REJECTED"
  | "REJECTED"
  | "APPROVED_FOR_DEV"
  | "DEV_IN_PROGRESS"
  | "DEV_READY"
  | "DEV_FAILED"
  | "MERGED"
  | "LIVE";

export const TICKET_STATUSES: TicketStatus[] = [
  "OPEN",
  "ANALYZING",
  "ANALYZED",
  "NEEDS_CLARIFICATION",
  "SUPPORT_QUEUE",
  "CONFLICT_REVIEW",
  "AUTO_REJECTED",
  "REJECTED",
  "APPROVED_FOR_DEV",
  "DEV_IN_PROGRESS",
  "DEV_READY",
  "DEV_FAILED",
  "MERGED",
  "LIVE",
];

const DEVELOPMENT_STATUSES: TicketStatus[] = [
  "DEV_IN_PROGRESS",
  "DEV_READY",
  "DEV_FAILED",
  "MERGED",
  "LIVE",
];

export function hasCodeDevelopment(ticket: Ticket): boolean {
  return Boolean(ticket.agentId || ticket.branchName || ticket.prUrl || ticket.developmentStartedAt)
    || DEVELOPMENT_STATUSES.includes(ticket.status);
}

export type TicketComment = {
  id: string;
  authorType: "USER" | "ADMIN" | "SYSTEM";
  authorEmail?: string;
  body: string;
  createdDate?: string | number[];
};

export type Ticket = {
  id: string;
  number: string;
  type: TicketType;
  status: TicketStatus;
  title: string;
  description: string;
  requesterEmail?: string;
  requesterUsername?: string;
  tenant?: string;
  analysis?: {
    difficulty?: string;
    impact?: string[];
    aiBudgetUsd?: number;
    estimatedHours?: number;
    benefitScore?: number;
    summary?: string;
    conflictScore?: number;
    conflictTicketNumber?: string;
    inScope?: boolean;
    targetProduct?: string;
    clarificationNeeded?: boolean;
    clarificationPrompt?: string;
  };
  agentId?: string;
  branchName?: string;
  prUrl?: string;
  runStatus?: string;
  developmentStartedAt?: string;
  mergedAt?: string;
  createdDate?: string | number[];
  comments?: TicketComment[];
};

export type SimilarMatch = { id: string; number: string; title: string; score: number };

export type CreateTicketResponse = {
  ticket?: Ticket;
  requiresConfirmation?: boolean;
  similar?: SimilarMatch[];
};

export type Quota = {
  ticketsRemaining?: number;
  ticketsUsed?: number;
  ticketsIncluded?: number;
  packageKey?: string;
};

export type Stats = {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  averageBenefitScore: number;
  openCount: number;
  mergedCount: number;
  agentSuccessCount: number;
  agentFailedCount: number;
  ticketsRemaining?: number;
};

export const ticketService = {
  list: () => getData<Ticket[]>("/tickets"),
  get: (id: string) => getData<Ticket>(`/tickets/${id}`),
  stats: () => getData<Stats>("/tickets/stats"),
  quota: () => getData<Quota>("/quota"),
  create: (body: { type: TicketType; title: string; description: string; confirmSimilar?: boolean }) =>
    postData<CreateTicketResponse>("/tickets", body),
  comment: (id: string, body: string) => postData<Ticket>(`/tickets/${id}/comments`, { body }),
  adminComment: (id: string, body: string) => postData<Ticket>(`/admin/tickets/${id}/comments`, { body }),
  reject: (id: string, reason: string) => postData<Ticket>(`/admin/tickets/${id}/reject`, { reason }),
  convert: (id: string) => postData<Ticket>(`/admin/tickets/${id}/convert`),
  approve: (id: string) => postData<Ticket>(`/admin/tickets/${id}/approve-development`),
  reopen: (id: string) => postData<Ticket>(`/admin/tickets/${id}/reopen`),
  retry: (id: string) => postData<Ticket>(`/admin/tickets/${id}/retry`),
  updateStatus: (id: string, status: TicketStatus) =>
    postData<Ticket>(`/admin/tickets/${id}/status`, { status }),
  delete: (id: string) => deleteData(`/admin/tickets/${id}`),
  adminStats: () => getData<Stats>("/admin/stats"),
};
